import { useMemo } from 'react';
import { queryPackingListItemsAll, type PackingListItemMinimal } from '../schema';
import { sortEventsByStartDateThenName } from '../utils/eventSort';

/** Minimal event shape for usePackingListsByEvent (id required; startDate/name/endDate for sort and filter). */
export type EventWithId = {
    id: string;
    startDate?: string | number | null;
    endDate?: string | number | null;
    name?: string | null;
};

/**
 * Query packing list items (flat) and derive packingListItemsByEvent + eventsWithPackingLists.
 * Used by CreateTripDialog, EditTripDialog, CreateOperationDialog.
 */
export function usePackingListsByEvent(
    events: EventWithId[],
    options?: { allowedEventIds?: string[]; enabled?: boolean }
): {
    packingListItemsByEvent: Map<string, PackingListItemMinimal[]>;
    eventsWithPackingLists: EventWithId[];
    isLoading: boolean;
    error: unknown;
} {
    const enabled = options?.enabled !== false;
    const { data, isLoading, error } = queryPackingListItemsAll(enabled);

    const packingListItemsByEvent = useMemo(() => {
        const map = new Map<string, PackingListItemMinimal[]>();
        if (!data?.packingListItems) return map;
        for (const item of data.packingListItems) {
            const eventId = item.event?.id;
            if (!eventId) continue;
            const list = map.get(eventId) ?? [];
            list.push(item);
            map.set(eventId, list);
        }
        return map;
    }, [data]);

    const eventsWithPackingLists = useMemo(() => {
        let list = events.filter(e => packingListItemsByEvent.has(e.id));
        if (options?.allowedEventIds !== undefined) {
            const allowed = new Set(options.allowedEventIds);
            list = list.filter(e => allowed.has(e.id));
        }
        return sortEventsByStartDateThenName(list) as EventWithId[];
    }, [events, packingListItemsByEvent, options]);

    return { packingListItemsByEvent, eventsWithPackingLists, isLoading, error };
}
